import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type TaskManagerType =
  | "todoist"
  | "linear"
  | "asana"
  | "trello"
  | "google_tasks"
  | null;

/**
 * A direct message that has been opened but never written to.
 *
 * `topic` is the pair-derived realtime topic, and it is also the slug the
 * channel will be created with — so the room keeps its identity when the first
 * message materializes it, rather than appearing to jump to a different
 * conversation.
 */
export interface PendingDirectMessage {
  peerId: string;
  topic: string;
  /** Enough to render the header without a second fetch. */
  peerName: string;
  peerImageUrl: string | null;
}

export interface SessionCompletionData {
  id: string;
  startedAt: string;
  endedAt: string | null;
  targetGoal: number;
  finalPoints: number;
  duration: number;
  tasksWorkedOn: number;
  tasksCompleted: number;
  bonusApplied?: { amount: number; bonusId?: string } | null;
  bonusAwarded?: { amount: number; usableOnDate?: string } | null;
}

const MAX_SESSION_POINTS = 1000;

function normalizeSessionCompletionData(
  data: SessionCompletionData
): SessionCompletionData {
  const clampPoints = (value: number | undefined): number => {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.max(0, Math.min(MAX_SESSION_POINTS, Math.round(n)));
  };

  return {
    ...data,
    finalPoints: clampPoints(data.finalPoints),
    bonusApplied: data.bonusApplied
      ? { ...data.bonusApplied, amount: clampPoints(data.bonusApplied.amount) }
      : data.bonusApplied,
    bonusAwarded: data.bonusAwarded
      ? { ...data.bonusAwarded, amount: clampPoints(data.bonusAwarded.amount) }
      : data.bonusAwarded,
  };
}

export type SettingsMenu =
  | "main"
  | "profile"
  | "smartAssistant"
  | "customizePomodoroTimer"
  | "enablePhoneWebsiteDistractions"
  | "websiteDistractions"
  | "desktopAppDistractions"
  | "connectCalendars"
  | "connectTaskManager"
  | "customizeTasks"
  | "emailPreferences"
  | "socialMedia"
  | "connectTaskManager-todoist"
  | "connectTaskManager-linear"
  | "connectTaskManager-asana"
  | "connectTaskManager-trello"
  | "connectTaskManager-google_tasks"
  | "keyboardShortcuts"
  | null;

interface SettingsStore {
  isSettingsOpen: boolean;
  activeSettingsMenu: SettingsMenu;
  menuHistory: SettingsMenu[];
  activeTaskManager: TaskManagerType;
  oauthProcessing: boolean;
  phoneDistractionDialogOpen: boolean;
  /** Bumped when the phone distraction dialog is opened; single-use sign-in tokens must never reuse. */
  phoneDistractionDialogQrKey: number;
  sessionCompletionData: SessionCompletionData | null;
  showCompletionDialog: boolean;
  /** Where the session completion celebration is shown (`pip` vs main dashboard). */
  sessionCompletionSurface: "main" | "pip" | null;
  activeTab: boolean;
  musicMuted: boolean;
  /** Temporary silence for meetings: timer bells, assistant TTS, and music output (Howler). Client-only. */
  meetingAudioSilence: boolean;
  /** When meeting silence was last toggled on this browser, so the member's extensions keep the newest (TX-5187). */
  meetingAudioSilenceChangedAt: number | null;
  activeChatChannelId: string | null;
  /**
   * A conversation the member has OPENED but that has no channel row yet.
   *
   * Opening a direct message writes nothing, so between the click and the first
   * message there is a real room with no id to name it by. This holds that room:
   * the peer to send to, and the topic both clients already subscribe to.
   *
   * Cleared the moment `activeChatChannelId` takes over, which happens when the
   * first message materializes the channel. The two are never both set — a
   * pending room and a real one are the same room in different states, not two
   * places a member could be.
   */
  pendingDm: PendingDirectMessage | null;
  setActiveTab: (state: boolean) => void;
  setPhoneDistractionDialogOpen: (state: boolean) => void;
  setSessionCompletionData: (data: SessionCompletionData | null) => void;
  setShowCompletionDialog: (open: boolean) => void;
  setSessionCompletionSurface: (surface: "main" | "pip" | null) => void;
  setActiveTaskManager: (taskManager: TaskManagerType) => void;
  openSettings: (menu?: SettingsMenu) => void;
  closeSettings: () => void;
  setActiveSettingsMenu: (menu: SettingsMenu) => void;
  setOauthProcessing: (state: boolean) => void;
  addToMenuHistory: (menu: SettingsMenu) => void;
  navigateBack: () => boolean;
  clearMenuHistory: () => void;
  navigateTo: (menu: SettingsMenu) => void;
  setMusicMuted: (muted: boolean) => void;
  setMeetingAudioSilence: (silence: boolean) => void;
  setActiveChatChannelId: (channelId: string | null) => void;
  /** Opens an empty conversation. Clears any real channel — see `pendingDm`. */
  setPendingDm: (pending: PendingDirectMessage | null) => void;
  /**
   * Swaps a pending conversation for the channel its first message created.
   *
   * One action rather than two setters, because the two halves must not be
   * observable apart: a render between them would show either a room with no
   * identity or two rooms at once.
   */
  promotePendingDm: (channelId: string) => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      isSettingsOpen: false,
      activeSettingsMenu: "main",
      menuHistory: ["main"],
      activeTaskManager: null,
      oauthProcessing: false,
      phoneDistractionDialogOpen: false,
      phoneDistractionDialogQrKey: 0,
      sessionCompletionData: null,
      showCompletionDialog: false,
      sessionCompletionSurface: null,
      activeTab: true,
      musicMuted: false,
      meetingAudioSilence: false,
      meetingAudioSilenceChangedAt: null,
      activeChatChannelId: null,
      pendingDm: null,
      setActiveTab: (state: boolean) => set({ activeTab: state }),
      setMusicMuted: (muted: boolean) => set({ musicMuted: muted }),
      setMeetingAudioSilence: (silence: boolean) =>
        set({ meetingAudioSilence: silence, meetingAudioSilenceChangedAt: Date.now() }),
      setActiveChatChannelId: (channelId: string | null) =>
        // Selecting a real channel leaves any empty room behind.
        set({ activeChatChannelId: channelId, pendingDm: null }),
      setPendingDm: (pending: PendingDirectMessage | null) =>
        set({ pendingDm: pending, activeChatChannelId: null }),
      promotePendingDm: (channelId: string) =>
        set({ activeChatChannelId: channelId, pendingDm: null }),
      setActiveTaskManager: (taskManager: TaskManagerType) =>
        set({ activeTaskManager: taskManager }),
      openSettings: (menu: SettingsMenu = "main") =>
        set({
          isSettingsOpen: true,
          activeSettingsMenu: menu,
        }),

      closeSettings: () =>
        set({
          isSettingsOpen: false,
          activeSettingsMenu: "main",
          activeTaskManager: null,
          menuHistory: ["main"],
          // Don't clear history on close to maintain navigation state
        }),

      setActiveSettingsMenu: (menu: SettingsMenu) =>
        set({
          activeSettingsMenu: menu,
          isSettingsOpen: true,
        }),

      setOauthProcessing: (state: boolean) =>
        set({
          oauthProcessing: state,
        }),

      setPhoneDistractionDialogOpen: (open: boolean) =>
        set((s) => {
          if (open) {
            const isOpening = !s.phoneDistractionDialogOpen;
            return {
              phoneDistractionDialogOpen: true,
              phoneDistractionDialogQrKey: isOpening
                ? s.phoneDistractionDialogQrKey + 1
                : s.phoneDistractionDialogQrKey,
            };
          }
          return { phoneDistractionDialogOpen: false };
        }),

      setSessionCompletionData: (data: SessionCompletionData | null) =>
        set({
          sessionCompletionData: data ? normalizeSessionCompletionData(data) : null,
        }),

      setShowCompletionDialog: (open: boolean) =>
        set({ showCompletionDialog: open }),

      setSessionCompletionSurface: (surface: "main" | "pip" | null) =>
        set({ sessionCompletionSurface: surface }),

      addToMenuHistory: (menu: SettingsMenu) =>
        set((state) => {
          // Only add to history if it's different from the current menu and not null
          if (menu && state.activeSettingsMenu !== menu) {
            const next = [
              ...state.menuHistory,
              state.activeSettingsMenu,
            ].filter(Boolean) as SettingsMenu[];
            return {
              menuHistory: next.slice(-25),
            };
          }
          return state;
        }),

      navigateTo: (menu: SettingsMenu) => {
        set((state) => {
          // Don't update if we're already on this menu
          if (state.activeSettingsMenu === menu) return state;

          const newHistory =
            state.activeSettingsMenu !== null &&
            state.activeSettingsMenu !== menu
              ? [...state.menuHistory, state.activeSettingsMenu]
              : [...state.menuHistory];

          const trimmed = newHistory.filter(Boolean) as SettingsMenu[];
          return {
            activeSettingsMenu: menu,
            isSettingsOpen: true,
            menuHistory: trimmed.slice(-25),
          };
        });
      },

      navigateBack: () => {
        let previousMenu: SettingsMenu | null = null;

        set((state) => {
          if (state.menuHistory.length === 0) return state;

          // Get and remove the last menu from history
          previousMenu = state.menuHistory[state.menuHistory.length - 1];
          const newHistory = state.menuHistory.slice(0, -1);
          if (newHistory.length === 0) {
            return {
              activeSettingsMenu: "main",
              menuHistory: ["main"],
            };
          }

          return {
            activeSettingsMenu: previousMenu,
            menuHistory: newHistory,
          };
        });

        return previousMenu !== null;
      },

      clearMenuHistory: () =>
        set({
          menuHistory: [],
        }),
    }),
    {
      name: "sukha-settings",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isSettingsOpen: state.isSettingsOpen,
        activeSettingsMenu: state.activeSettingsMenu,
        menuHistory: state.menuHistory,
        musicMuted: state.musicMuted,
        meetingAudioSilence: state.meetingAudioSilence,
        meetingAudioSilenceChangedAt: state.meetingAudioSilenceChangedAt,
        activeChatChannelId: state.activeChatChannelId,
        // `pendingDm` is deliberately NOT persisted. An empty conversation holds
        // nothing worth restoring, and a stored peer id outlives the peer —
        // reloading into a room for a deleted account is a worse outcome than
        // landing back on the default channel.
      }),
    }
  )
);
